import { Signal } from "@suse-o11y/signal";
import React from "react";

import { SkeletonText } from "~/components/skeleton/text";
import { Truncated } from "~/components/truncated";
import { cx } from "~/tools/class-variance";
import type { Time } from "~/tools/date-time/time";
import { first } from "~/tools/first";
import { type FormatterUnit, formatUnits, toFormatterUnit } from "~/tools/formatter-unit";
import { isNonEmptyArray } from "~/tools/non-empty-array";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import type { BoundMetricId } from "../../common/openapi";
import { isHTTPError } from "../../generic/request";
import { type BatchedPromQLRequest, queryInstantPromQL } from "../../queries/metrics/batch";
import { useQuery } from "../../queries/react-query";
import { useExplorationDrawerState } from "../react/drawer/use-exploration-drawer-state";

import { CellError } from "./_cell-error";
import { NotAvailable } from "./_not-available";

export interface MetricsValueProps extends BatchedPromQLRequest {
	className?: string;
	componentIdentifier: undefined | string;
	boundMetricId?: BoundMetricId;
	query: undefined | string;
	step: undefined | number | string;
	decimalPlaces: undefined | number;
	unit: undefined | FormatterUnit;
	timestamp: undefined | Time;
	timeRange: undefined | TimeRange;
	title: string;
}

export const MetricsValue: React.FC<MetricsValueProps> = ({
	className,
	componentIdentifier,
	boundMetricId,
	batch,
	step,
	query,
	unit,
	decimalPlaces,
	timestamp,
	timeRange,
	title,
}) => {
	const explorationDrawer = useExplorationDrawerState();

	const { error, data } = useQuery({
		enabled: Boolean(step) && query != null,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		refetchOnTimelineSummaryUpdate: true,

		...queryInstantPromQL({
			batch,
			step: step ?? "",
			query: query ?? "",
			timestamp,
		}),
	});

	if (error != null) {
		return (
			<div className={cx("leading-none", className)}>
				<CellError message={isHTTPError(error) ? error.message : "Unexpected error"} />
			</div>
		);
	}

	return (
		<SkeletonText className={cx("leading-none", className)} when={data}>
			{(result) => {
				if (result.isFailure()) {
					return <CellError message={result.error} />;
				}

				if (!isNonEmptyArray(result.value)) {
					return <NotAvailable />;
				}

				const [, value] = first(result.value).value;

				const formattedValue = formatUnits(toFormatterUnit(unit), value, { precision: decimalPlaces });

				if (componentIdentifier != null && timeRange != null && query != null) {
					return (
						<button
							className="link link-secondary"
							onClick={(event) => {
								// stop event from bubbling into the row-link
								event.stopPropagation();

								if (boundMetricId) {
									explorationDrawer.openBoundMetrics({
										componentIdOrIdentifier: componentIdentifier,
										boundMetricId,
										timestamp,
										timeRange: Signal(timeRange),
										title,
									});
								} else {
									explorationDrawer.openMetrics({
										componentIdOrIdentifier: componentIdentifier,
										timestamp,
										timeRange: Signal(timeRange),
										chart: {
											kind: "TimeSeriesChart",
											settings: { unit },
										},
										metricQueries: [
											{
												promql: query,
											},
										],
										title,
									});
								}
							}}
							type="button"
						>
							<Truncated tooltip="Open in metric preview">{formattedValue}</Truncated>
						</button>
					);
				}
				return <Truncated>{formattedValue}</Truncated>;
			}}
		</SkeletonText>
	);
};
